import { NetworkStatus } from "@apollo/client";
import { useCallback } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { User } from "components/icons/generated";
import MetricCard from "components/MetricCard";
import MissingDataBanner from "components/MissingDataBanner";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Icon from "ds/components/Icon";
import { AnalyticsPageDashboard } from "hooks/useAnalytics/pages/dashboard";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import Timestamp from "components/Timestamp";
import Card from "ds/components/Card";
import Typography from "ds/components/Typography";
import CollapsibleSectionWrapper from "ds/components/CollapsibleSection/components/Wrapper";
import CollapsibleSectionHeader from "ds/components/CollapsibleSection/components/Header";
import CollapsibleSectionContent from "ds/components/CollapsibleSection/components/Content";

import { DashboardContext } from "../../context";
import { OverviewStaticWidget } from "../../types";
import { getLastLogins } from "./getLastLogins";
import { GET_USERS_ACTIVITY } from "./gql";
import DashboardOverviewColumnUserActivitySkeleton from "./Skeleton";
import styles from "./styles.module.css";
import { GetUsersActivity } from "./types";

const DashboardOverviewColumnUserActivity = () => {
  const { updateTabOverviewColumnConfig, config } = useTypedContext(DashboardContext);

  const [isCollapsed, handleToggle] = useToggle(true);

  const { loading, data, error, refetch, networkStatus } = usePolledQuery<GetUsersActivity>(
    GET_USERS_ACTIVITY,
    {
      pollingGroup: PollingIntervalGroups.Dashboard,
    }
  );

  const showErrorBanner = !!error;

  const isLoading = (loading && !data?.usage?.logins) || networkStatus === NetworkStatus.refetch;

  const usersCount = showErrorBanner ? 0 : data?.usage?.logins?.length || 0;

  const lastLogins = getLastLogins(data?.usage?.logins);

  const hasLastLogins = !showErrorBanner && !!lastLogins?.length;

  const onHide = () => {
    if (config["overview"].overviewColumn) {
      updateTabOverviewColumnConfig(
        config["overview"].overviewColumn.map((item) =>
          item.value === OverviewStaticWidget.UserActivity ? { ...item, hidden: true } : item
        )
      );
    }
  };

  const onToggle = useCallback(() => {
    handleToggle();
  }, [handleToggle]);

  return (
    <Card
      title="Users active in the last 28 days"
      headerSuffix={
        <DropdownMenuEllipsis tooltip="Settings" buttonVariant="ghost">
          <DropdownMenuItem onAction={onHide}>Hide</DropdownMenuItem>
          <DropdownMenuItem href="/settings/users">Go to Identity Access Management</DropdownMenuItem>
        </DropdownMenuEllipsis>
      }
    >
      {!showErrorBanner && isLoading ? (
        <DashboardOverviewColumnUserActivitySkeleton />
      ) : (
        <Box direction="column">
          {showErrorBanner && (
            <Box margin="0 0 x-large 0" fullWidth>
              <MissingDataBanner
                text="Couldn’t load user data. Please try to refresh or come back later. "
                refreshHandler={refetch}
                fullWidth
              />
            </Box>
          )}
          <MetricCard
            noBorder
            titleIcon={<Icon src={User} />}
            action={
              <LinkButton
                size="medium"
                variant="primary"
                analyticsTitle="Users Widget - Invite Users Clicked"
                analyticsPage={AnalyticsPageDashboard.Dashboard}
                to="/settings/users"
              >
                Invite user
              </LinkButton>
            }
            titleColor="primary"
            title="Users"
            value={usersCount}
            className={styles.purpleWeakBackground}
          />
          {hasLastLogins && (
            <CollapsibleSectionWrapper
              onToggle={onToggle}
              isCollapsed={isCollapsed}
              className={styles.collapsibleSection}
            >
              <CollapsibleSectionHeader
                ariaLevel={3}
                ariaLabel="Last logins"
                className={styles.collapsibleSectionHeader}
                titleVariant="p-t7"
              >
                Last logins
              </CollapsibleSectionHeader>
              <CollapsibleSectionContent fullbleed gap="medium">
                <Box direction="column" gap="medium" padding="x-large 0 0 0">
                  {lastLogins.map((item) => (
                    <Card key={item.username} contentGap="medium">
                      <Typography tag="p" variant="p-t7">
                        {item.username}
                      </Typography>
                      <Box align="center">
                        <Timestamp timestamp={item.timestamp} />
                      </Box>
                    </Card>
                  ))}
                </Box>
              </CollapsibleSectionContent>
            </CollapsibleSectionWrapper>
          )}
        </Box>
      )}
    </Card>
  );
};

export default DashboardOverviewColumnUserActivity;
